import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, FolderInput, Pause, Play, PlugZap, RefreshCw, Server, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { ChannelBackorderCard } from "@/components/BackorderSettings";
import { ShippingMappingCard } from "@/components/ShippingMappingCard";
import { Badge, Button, Card, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, type SftpConfig } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
import { serverText } from "@/lib/server-text";
const EMPTY: SftpConfig = { host: "", port: 22, username: "", authType: "password", inboundDir: "/out", archiveDir: "/out/archiv", errorDir: "/out/fehler", outboundDir: "/in", templateId: null, exportShipments: true };
const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehler") : "Fehler");

/** Formular: Zugang, Ordner, Vorlage, Rückmeldung – für Einrichten und Ändern */
function SftpForm({ name, setName, cfg, setCfg, secret, setSecret, hasSecret }: { name: string; setName: (v: string) => void; cfg: SftpConfig; setCfg: (c: SftpConfig) => void; secret: string; setSecret: (v: string) => void; hasSecret?: boolean }) {
  const { tenantId } = useTenant();
  const templates = useQuery({ queryKey: ["csv-templates", tenantId, "orders"], queryFn: () => api.imports.templates(tenantId, "orders") });
  const set = <K extends keyof SftpConfig>(k: K, v: SftpConfig[K]) => setCfg({ ...cfg, [k]: v });
  const field = (label: string, el: React.ReactNode, hint?: string) => (
    <label className="block">
      <span className="text-[12px] font-medium text-fg-muted">{t(label)}</span>
      <div className="mt-1">{el}</div>
      {hint && <span className="text-[11.5px] text-fg-subtle">{t(hint)}</span>}
    </label>
  );
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        {field("Name", <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("z. B. ERP-Export")} />)}
        <div className="sm:col-span-2 grid grid-cols-[1fr_88px] gap-2">
          {field("Server", <Input value={cfg.host} onChange={(e) => set("host", e.target.value.trim())} placeholder={t("sftp.kunde.de")} className="font-mono" />)}
          {field("Port", <Input value={String(cfg.port)} onChange={(e) => set("port", Number(e.target.value.replace(/\D/g, "")) || 22)} inputMode="numeric" />)}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {field("Benutzer", <Input value={cfg.username} onChange={(e) => set("username", e.target.value.trim())} className="font-mono" />)}
        {field("Anmeldung", (
          <select value={cfg.authType} onChange={(e) => set("authType", e.target.value as SftpConfig["authType"])} className="h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
            <option value="password">{t("Passwort")}</option>
            <option value="key">{t("Privater Schlüssel")}</option>
          </select>
        ))}
        {cfg.authType === "password" && field("Passwort", <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={hasSecret ? t("unverändert") : ""} autoComplete="new-password" />, hasSecret ? "Leer lassen = gespeichertes behalten" : undefined)}
      </div>
      {cfg.authType === "key" && field("Privater Schlüssel (OpenSSH)", <textarea value={secret} onChange={(e) => setSecret(e.target.value)} rows={4} placeholder={hasSecret ? t("unverändert – leer lassen = gespeicherten Schlüssel behalten") : t("-----BEGIN OPENSSH PRIVATE KEY-----")} className="w-full rounded-lg border border-border bg-surface p-2.5 font-mono text-[11.5px]" />)}
      <div className="grid sm:grid-cols-2 gap-3">
        {field("Eingang: Aufträge abholen aus", <Input value={cfg.inboundDir} onChange={(e) => set("inboundDir", e.target.value)} className="font-mono" />, "Alle *.csv in diesem Ordner")}
        {field("Ausgang: Versandbestätigungen nach", <Input value={cfg.outboundDir} onChange={(e) => set("outboundDir", e.target.value)} className="font-mono" disabled={!cfg.exportShipments} />, "versand_JJJJMMTT-HHMMSS.csv mit Tracking")}
        {field("Archiv (verarbeitete Dateien)", <Input value={cfg.archiveDir} onChange={(e) => set("archiveDir", e.target.value)} className="font-mono" />)}
        {field("Fehler (unbrauchbare Dateien + Hinweise)", <Input value={cfg.errorDir} onChange={(e) => set("errorDir", e.target.value)} className="font-mono" />)}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 items-end">
        {field("Spaltenzuordnung", (
          <select value={cfg.templateId ?? ""} onChange={(e) => set("templateId", e.target.value || null)} className="h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
            <option value="">{t("Logiful-Vorlage")}</option>
            {templates.data?.map((tv) => <option key={tv.id} value={tv.id}>{t("Vorlage:")} {tv.name}</option>)}
          </select>
        ), "Eigene Vorlagen entstehen beim CSV-Import unter Aufträge")}
        <label className="flex items-center gap-2 text-[13px] cursor-pointer h-9">
          <input type="checkbox" className="accent-ink" checked={cfg.exportShipments} onChange={(e) => set("exportShipments", e.target.checked)} />
          {t("Versandbestätigungen mit Tracking zurückschreiben")}
        </label>
      </div>
    </div>
  );
}

/** Neue SFTP-Anbindung: Verbindung testen, dann anbinden */
export function SftpConnectDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [name, setName] = useState("ERP-Export");
  const [cfg, setCfg] = useState<SftpConfig>(EMPTY);
  const [secret, setSecret] = useState("");
  const [tested, setTested] = useState<{ ok: boolean; waitingFiles: number; problems: string[] } | null>(null);
  const ready = cfg.host && cfg.username && secret;
  const test = useMutation({ mutationFn: () => api.channels.testSftp(tenantId, { config: cfg, secret }), onSuccess: setTested, onError: (e) => setTested({ ok: false, waitingFiles: 0, problems: [errMsg(e)] }) });
  const create = useMutation({
    mutationFn: () => api.channels.createSftp(tenantId, { name, config: cfg, secret }),
    onSuccess: (r) => {
      toast.success("SFTP-Anbindung eingerichtet", { description: r.waitingFiles ? `${r.waitingFiles} Dateien warten – werden jetzt abgeholt.` : "Neue Dateien werden alle 5 Minuten abgeholt." });
      void qc.invalidateQueries({ queryKey: ["channels", tenantId] });
      onOpenChange(false);
      nav(`/channels/${r.id}`);
    },
    onError: (e) => toast.error("Nicht angebunden", { description: errMsg(e) }),
  });
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content aria-describedby={undefined} className="fixed z-50 inset-x-4 top-[5vh] mx-auto max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-surface shadow-2xl animate-pop-in">
          <div className="px-6 pt-5 pb-4 border-b border-border flex items-start">
            <div>
              <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><Server className="size-4 text-fg-subtle" /> {t("SFTP-Anbindung")}</Dialog.Title>
              <p className="text-fg-muted mt-0.5 text-[13px]">{t("Aufträge als CSV automatisch abholen (alle 5 Minuten) und Versandbestätigungen mit Tracking zurückschreiben.")}</p>
            </div>
            <Dialog.Close className="ml-auto size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <SftpForm name={name} setName={setName} cfg={cfg} setCfg={(c) => { setCfg(c); setTested(null); }} secret={secret} setSecret={(s) => { setSecret(s); setTested(null); }} />
            {tested && (
              <div className={cx("mt-4 rounded-xl border p-3 text-[13px]", tested.ok ? "border-success/30 bg-success-soft/50" : "border-danger/30 bg-danger-soft/50")}>
                {tested.ok ? <p className="flex items-center gap-2"><CheckCircle2 className="size-4 text-success" /> {t("Verbindung steht ·")} {fmtNumber(tested.waitingFiles)} {t("CSV-Dateien im Eingang")}</p> : <p className="flex items-start gap-2"><CircleAlert className="size-4 text-danger mt-0.5" /> {tested.problems.join(" · ")}</p>}
              </div>
            )}
          </div>
          <div className="px-6 h-16 border-t border-border flex items-center justify-end gap-2">
            <Button disabled={!ready} loading={test.isPending} onClick={() => test.mutate()}>{t("Verbindung testen")}</Button>
            <Button variant="primary" disabled={!ready || !name.trim()} loading={create.isPending} onClick={() => create.mutate()}>{t("Anbinden")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Detailseite einer SFTP-Anbindung: Status, Protokoll, Einstellungen, Versandarten-Zuordnung, Fehlbestand */
export function SftpChannelDetail({ channelId, shippingMapping, seen, backorder }: { channelId: string; shippingMapping: Parameters<typeof ShippingMappingCard>[0]["mapping"]; seen: { name: string; count: number }[]; backorder: Parameters<typeof ChannelBackorderCard>[0]["override"] }) {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const editable = canManage(role);
  const q = useQuery({ queryKey: ["sftp", tenantId, channelId], queryFn: () => api.channels.sftp(tenantId, channelId), refetchInterval: 15_000 });
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState("");
  const [cfg, setCfg] = useState<SftpConfig>(EMPTY);
  const [secret, setSecret] = useState("");
  const refresh = () => void qc.invalidateQueries({ queryKey: ["sftp", tenantId, channelId] });
  const run = useMutation({ mutationFn: () => api.channels.runSftp(tenantId, channelId), onSuccess: () => { toast.success("Wird abgeholt", { description: msg("Das Protokoll aktualisiert sich gleich.") }); setTimeout(refresh, 4000); } });
  const pause = useMutation({ mutationFn: (active: boolean) => api.channels.pauseSftp(tenantId, channelId, active), onSuccess: (r) => { toast.success(r.active ? "Abholung aktiv" : "Abholung pausiert"); refresh(); void qc.invalidateQueries({ queryKey: ["channels", tenantId] }); } });
  const save = useMutation({
    mutationFn: () => api.channels.updateSftp(tenantId, channelId, { name, config: cfg, ...(secret ? { secret } : {}) }),
    onSuccess: () => { toast.success("Gespeichert – Verbindung geprüft"); setEdit(false); setSecret(""); refresh(); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  const d = q.data;
  if (!d) return <Skeleton className="h-60 w-full" />;
  const active = d.status === "active";
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-start gap-3">
          <div className="size-10 rounded-xl bg-surface-2 grid place-items-center"><Server className="size-5 text-fg-muted" /></div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{d.name}</p>
            <p className="text-[12px] text-fg-muted font-mono truncate">{d.config.username}@{d.config.host}:{d.config.port} · {d.config.inboundDir}</p>
          </div>
          {active ? <Badge tone="success">{t("aktiv · alle 5 Min.")}</Badge> : <Badge tone="warning">{t("pausiert")}</Badge>}
        </div>
        {(d.lastError || d.log?.lastError) && <p className="mt-3 text-[12.5px] text-danger bg-danger-soft rounded-lg px-3 py-2">{d.log?.lastError ?? d.lastError}</p>}
        <div className="mt-3 flex flex-wrap gap-2 text-[12.5px] text-fg-muted">
          <span>{t("Zuletzt abgeholt:")} {d.log?.lastRunAt ? fmtRelative(d.log.lastRunAt) : t("noch nie")}</span>
          {d.log?.lastExport && <span>{t("· Versandbestätigung")} {fmtRelative(d.log.lastExport.at)} ({fmtNumber(d.log.lastExport.shipments)} {t("Sendungen,")} {d.log.lastExport.name})</span>}
        </div>
        {editable && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" icon={<RefreshCw className="size-3.5" />} disabled={!active} loading={run.isPending} onClick={() => run.mutate()}>{t("Jetzt abholen")}</Button>
            <Button size="sm" variant="ghost" icon={active ? <Pause className="size-3.5" /> : <Play className="size-3.5" />} onClick={() => pause.mutate(!active)}>{active ? t("Pausieren") : t("Fortsetzen")}</Button>
            <Button size="sm" variant="ghost" icon={<PlugZap className="size-3.5" />} onClick={() => { setName(d.name); setCfg(d.config); setSecret(""); setEdit(!edit); }}>{edit ? t("Einstellungen schließen") : t("Einstellungen")}</Button>
          </div>
        )}
        {edit && (
          <div className="mt-4 pt-4 border-t border-border">
            <SftpForm name={name} setName={setName} cfg={cfg} setCfg={setCfg} secret={secret} setSecret={setSecret} hasSecret={d.hasSecret} />
            <div className="mt-4 flex justify-end"><Button variant="primary" size="sm" loading={save.isPending} onClick={() => save.mutate()}>{t("Prüfen & speichern")}</Button></div>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="font-semibold text-[14px] flex items-center gap-2"><FolderInput className="size-4 text-fg-muted" /> {t("Abgeholte Dateien")}</h2>
        {!d.log?.files.length ? (
          <p className="mt-2 text-[13px] text-fg-muted">{t("Noch keine Datei abgeholt. Lege eine CSV-Datei in")} {d.config.inboundDir} {t("– sie wird innerhalb von 5 Minuten verarbeitet.")}</p>
        ) : (
          <table className="mt-3 w-full text-[13px]">
            <thead className="text-[12px] text-fg-subtle"><tr><th className="text-left font-medium py-1">{t("Datei")}</th><th className="text-left font-medium">{t("Wann")}</th><th className="text-right font-medium">{t("Aufträge")}</th><th className="text-right font-medium">{t("Schon da")}</th><th className="text-right font-medium">{t("Hinweise")}</th></tr></thead>
            <tbody>
              {d.log.files.map((f, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="py-1.5"><span className="font-mono text-[12px]">{f.name}</span>{f.message && <span className="block text-[11.5px] text-danger">{serverText(f.message)}</span>}</td>
                  <td className="text-fg-muted">{fmtRelative(f.at)}</td>
                  <td className="text-right tabular-nums">{f.result === "error" ? <Badge tone="danger">{t("Fehler")}</Badge> : fmtNumber(f.created)}</td>
                  <td className="text-right tabular-nums text-fg-muted">{fmtNumber(f.duplicates)}</td>
                  <td className={cx("text-right tabular-nums", f.issues ? "text-warning" : "text-fg-muted")}>{fmtNumber(f.issues)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mt-2 text-[12px] text-fg-subtle">{t("Fehlerhafte Zeilen stehen als „…hinweise.csv“ im Fehlerordner")} {d.config.errorDir}{t("; unbrauchbare Dateien werden dorthin verschoben.")}</p>
      </Card>

      <ShippingMappingCard key={JSON.stringify(shippingMapping)} channelId={channelId} mapping={shippingMapping} seen={seen} editable={editable} sourceLabel="Datei" />
      <ChannelBackorderCard channelId={channelId} override={backorder} />
    </div>
  );
}
